import { useState } from 'react';
import { INBOUND_STATUS, inboundStatusLabel } from '@/infrastructure/FulfillmentAdminClient';
import type { InboundRequestSummary } from '@/infrastructure/FulfillmentAdminClient';
import { fulfillmentSourceLabel } from '@/utils/fulfillmentSource';

/** Finished and cancelled requests are not waiting for anything, so the scan hub does not count them. */
const isOpen = (request: InboundRequestSummary): boolean =>
  request.status !== INBOUND_STATUS.RECEIVED && request.status !== INBOUND_STATUS.CANCELLED;

/** A box whose label survived the trip is the one the desk can scan, so it is offered first. */
const trackingFirst = (a: InboundRequestSummary, b: InboundRequestSummary): number =>
  Number(Boolean(b.tracking)) - Number(Boolean(a.tracking));

export interface ExpectedInboundPanelProps {
  requests: InboundRequestSummary[];
  /** Opens the request on the View Orders receive screen, so reconciling it never leaves the scan hub. */
  onOpen: (request: InboundRequestSummary) => void;
}

/**
 * The one-line summary of stock still on its way, collapsed by default so this screen stays a scan
 * hub. Opening a row is how a bulk box with a destroyed tracking label gets reconciled without a scan.
 */
export function ExpectedInboundPanel({ requests, onOpen }: ExpectedInboundPanelProps) {
  const [open, setOpen] = useState(false);
  const pending = requests.filter(isOpen);
  if (pending.length === 0) {
    return null;
  }

  const withTracking = pending.filter((request) => request.tracking).length;
  const partial = pending.filter((request) => request.status === INBOUND_STATUS.PARTIAL).length;
  const rows = [...pending].sort(trackingFirst);

  return (
    <div className="expected">
      <button type="button" className="btn btn-line btn-sm" onClick={() => setOpen((was) => !was)}>
        {open ? '▾' : '▸'} Expected Inbound {pending.length} — {withTracking} with tracking · {partial} Partial Inbound
      </button>
      {open && (
        <div className="table-wrap">
          <table className="tbl" aria-label="Expected inbound">
            <thead>
              <tr>
                <th>Inbound No.</th>
                <th>Sourcing Route</th>
                <th>Supplier</th>
                <th>Items</th>
                <th>Tracking No</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((request) => (
                <tr
                  key={request.docId}
                  tabIndex={0}
                  aria-label={`Reconcile inbound ${request.inboundNo}`}
                  onClick={() => onOpen(request)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      onOpen(request);
                    }
                  }}
                >
                  <td className="num">{request.inboundNo}</td>
                  <td>
                    <span className="tag tag-route">{fulfillmentSourceLabel(request.source)}</span>
                  </td>
                  <td>{request.supplier ?? '–'}</td>
                  <td className="num">{request.expectedQty != null ? `${request.expectedQty} units` : '–'}</td>
                  <td className="num track">{request.tracking ?? '–'}</td>
                  <td>
                    <span className="tag tag-pending">{request.partialNote ?? inboundStatusLabel(request.status)}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mut">
            Rows with tracking sorted to top · click a row to reconcile a box whose label did not survive the trip.
          </p>
        </div>
      )}
    </div>
  );
}
